import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Stats, Opportunity, Pitch } from "../lib/utils";
import { formatRelativeTime, getStatusColor, getMatchScoreColor, getMatchScoreBg, truncate, getPitchTypeColor, getPitchTypeLabel } from "../lib/utils";
import { Card, Badge, Button, LoadingSpinner, EmptyState } from "../components/ui";

export function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentOpportunities, setRecentOpportunities] = useState<Opportunity[]>([]);
  const [recentPitches, setRecentPitches] = useState<Pitch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [statsData, oppsData, pitchesData] = await Promise.all([
        api.stats.get(),
        api.opportunities.list({ status: "matched", limit: 5 }),
        api.pitches.list({ status: "draft", limit: 5 })
      ]);
      setStats(statsData);
      setRecentOpportunities(oppsData.opportunities);
      setRecentPitches(pitchesData.pitches);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const triggerFetch = async () => {
    try {
      await api.triggers.fetch();
      await loadDashboard();
    } catch (err) {
      alert("Failed to trigger fetch: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Overview of your opportunity pipeline</p>
        </div>
        <Button onClick={triggerFetch} icon="refresh" className="gap-2">
          Refresh Data
        </Button>
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50 text-red-700">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Opportunities"
          value={stats?.opportunities_by_status.reduce((sum, s) => sum + s.count, 0) || 0}
          icon="Briefcase"
          color="blue"
        />
        <StatCard
          title="Companies Tracked"
          value={stats?.total_companies || 0}
          icon="Building2"
          color="green"
        />
        <StatCard
          title="Draft Pitches"
          value={stats?.pitches_by_status.find(s => s.status === "draft")?.count || 0}
          icon="FileText"
          color="purple"
        />
        <StatCard
          title="Sent Pitches"
          value={stats?.pitches_by_status.find(s => s.status === "sent")?.count || 0}
          icon="Send"
          color="amber"
        />
      </div>

      {/* Recent Opportunities & Pitches */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Recent Matched Opportunities</h2>
            <Link to="/opportunities" className="text-sm text-primary-600 hover:text-primary-700">View all</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recentOpportunities.length === 0 ? (
              <EmptyState icon="Briefcase" title="No matched opportunities yet" description="Run a fetch cycle to discover opportunities" />
            ) : (
              recentOpportunities.map((opp) => (
                <Link key={opp.id} to={`/opportunities/${opp.id}`} className="block p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 truncate">{opp.title}</h3>
                      <p className="text-sm text-slate-500 mt-1 truncate">{opp.company_name || "Unknown company"}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getMatchScoreBg(opp.match_score)} ${getMatchScoreColor(opp.match_score)}`}>
                          {(opp.match_score * 100).toFixed(0)}% match
                        </span>
                        <span>{formatRelativeTime(opp.fetched_at)}</span>
                        <span className={`badge ${getStatusColor(opp.status)}`}>{opp.status}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Pitches Needing Review</h2>
            <Link to="/pitches" className="text-sm text-primary-600 hover:text-primary-700">View all</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recentPitches.length === 0 ? (
              <EmptyState icon="FileText" title="No draft pitches" description="Matched opportunities will generate pitches automatically" />
            ) : (
              recentPitches.map((pitch) => (
                <div key={pitch.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`badge ${getPitchTypeColor(pitch.pitch_type)}`}>{getPitchTypeLabel(pitch.pitch_type)}</span>
                        <span className={`badge ${getStatusColor(pitch.status)}`}>{pitch.status}</span>
                      </div>
                      <h3 className="font-medium text-slate-900 truncate">{pitch.subject}</h3>
                      <p className="text-sm text-slate-500 mt-1 truncate">{truncate(pitch.body, 100)}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getMatchScoreBg(pitch.match_score)} ${getMatchScoreColor(pitch.match_score)}`}>
                          {(pitch.match_score * 100).toFixed(0)}% match
                        </span>
                        <span>{formatRelativeTime(pitch.created_at)}</span>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => window.open(`/pitches/${pitch.id}`, '_blank')}>Review</Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Top Sources */}
      {stats?.top_sources && stats.top_sources.length > 0 && (
        <Card>
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Top Sources</h2>
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              {stats.top_sources.slice(0, 10).map((source) => (
                <span key={source.source} className="badge bg-slate-100 text-slate-700">
                  {source.source} <span className="ml-1 text-slate-500">({source.count})</span>
                </span>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function StatCard({ title, value, icon, color }: { title: string; value: number; icon: string; color: string }) {
  const colors: Record<string, string> = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    purple: "bg-purple-500",
    amber: "bg-amber-500",
    red: "bg-red-500"
  };
  
  const icons: Record<string, React.ComponentType<{ className?: string }>> = {
    Briefcase: () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    Building2: () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    FileText: () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
    Send: () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
  };
  
  const Icon = icons[icon] || icons.Briefcase;
  
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{value.toLocaleString()}</p>
        </div>
        <div className={`p-3 rounded-xl ${colors[color] || colors.blue}`}>
          <Icon className="text-white" />
        </div>
      </div>
    </Card>
  );
}